import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Minus, Send, Check, XCircle, Loader2, Trash2, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIkalStore } from '@/store/ikal.store';
import { useIkalStream } from '@/hooks/useIkalStream';
import { useChatStore } from '@/store/chat.store';
import { IkalOrb, type OrbState } from './IkalOrb';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const hasSpeechRecognition =
  typeof window !== 'undefined' &&
  ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

const hasSpeechSynthesis =
  typeof window !== 'undefined' && 'speechSynthesis' in window;

function stripMarkdown(text: string) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/^[-*•]\s+/gm, '')
    .trim();
}

// ─── Markdown renderer ────────────────────────────────────────────────────────

function Markdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  let listItems: string[] = [];

  function flushList() {
    if (!listItems.length) return;
    nodes.push(
      <ul key={`ul-${nodes.length}`} className="list-disc list-inside space-y-0.5 mb-2">
        {listItems.map((item, i) => (
          <li key={i} className="text-sm leading-relaxed">{renderInline(item)}</li>
        ))}
      </ul>,
    );
    listItems = [];
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^[-*•]\s/.test(line)) { listItems.push(line.replace(/^[-*•]\s/, '')); continue; }
    flushList();
    if (!line.trim()) {
      nodes.push(<br key={`br-${i}`} />);
    } else if (/^#{1,3}\s/.test(line)) {
      nodes.push(<p key={i} className="text-sm font-semibold mt-2 mb-0.5 text-gray-900">{renderInline(line.replace(/^#+\s/, ''))}</p>);
    } else if (/^```/.test(line)) {
      const end = lines.findIndex((l, j) => j > i && l.startsWith('```'));
      const code = lines.slice(i + 1, end > 0 ? end : undefined).join('\n');
      nodes.push(<pre key={i} className="bg-gray-100 rounded-lg p-2 text-xs font-mono overflow-x-auto my-1">{code}</pre>);
      if (end > 0) i = end;
    } else {
      nodes.push(<p key={i} className="text-sm leading-relaxed">{renderInline(line)}</p>);
    }
  }
  flushList();
  return <div className="space-y-0.5">{nodes}</div>;
}

function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
    if (/^`[^`]+`$/.test(part)) return <code key={i} className="bg-gray-100 px-1 rounded text-xs font-mono">{part.slice(1, -1)}</code>;
    return part;
  });
}

// ─── Confirm card ─────────────────────────────────────────────────────────────

function IkalConfirmCard() {
  const pendingProposal = useIkalStore((s) => s.pendingProposal);
  const { confirmar } = useIkalStream();
  if (!pendingProposal) return null;
  return (
    <div className="mx-3 my-2 p-3 rounded-xl border flex flex-col gap-2"
      style={{ borderColor: 'rgba(111,187,175,0.35)', backgroundColor: 'rgba(111,187,175,0.08)' }}>
      <div className="flex items-start gap-2">
        <div className="w-3.5 h-3.5 rounded-full mt-0.5 flex-shrink-0" style={{ backgroundColor: '#6FBBAF' }} />
        <p className="text-xs font-medium leading-snug text-gray-800">{pendingProposal.humanReadable}</p>
      </div>
      <p className="text-[11px]" style={{ color: '#568782' }}>¿Confirmas esta acción?</p>
      <div className="flex gap-2">
        <button
          onClick={() => confirmar(true)}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-white text-xs font-medium transition-colors"
          style={{ backgroundColor: '#3d5e5a' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2e4a47'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#3d5e5a'; }}
        >
          <Check size={12} /> Confirmar
        </button>
        <button
          onClick={() => confirmar(false)}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors"
        >
          <XCircle size={12} /> Cancelar
        </button>
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export function IkalPanel() {
  const {
    open, messages, streaming, streamBuffer,
    closeIkal, resetConversacion, contexto,
    queuedMessage, clearQueuedMessage,
  } = useIkalStore();
  const chatOpen = useChatStore((s) => s.open);
  const { send } = useIkalStream();

  const [texto, setTexto] = useState('');
  const [minimizado, setMinimizado] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceOutput, setVoiceOutput] = useState(true);

  const bottomRef    = useRef<HTMLDivElement>(null);
  const inputRef     = useRef<HTMLTextAreaElement>(null);
  const recognRef    = useRef<any>(null);
  const finalTextRef = useRef('');
  const lastSpokenId = useRef<string | null>(null);
  const voiceModeRef = useRef(false);

  // Mantener ref sincronizada con state (para closures en callbacks de voz)
  useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);

  // Scroll al fondo
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streamBuffer]);

  // Abrir panel: quitar minimizado
  useEffect(() => { if (open) setMinimizado(false); }, [open]);

  // Auto-envío de mensaje en cola (desde transcripción, contexto consulta, etc.)
  useEffect(() => {
    if (open && queuedMessage && !streaming) {
      const msg = queuedMessage;
      clearQueuedMessage();
      send(msg);
    }
  }, [open, queuedMessage, streaming, send, clearQueuedMessage]);

  // TTS: Ikal habla cuando llega un mensaje nuevo de assistant
  useEffect(() => {
    if (!voiceOutput || !hasSpeechSynthesis) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.id === lastSpokenId.current) return;
    lastSpokenId.current = last.id;
    speakIkal(last.text);
  }, [messages, voiceOutput]);

  // Al cerrar, limpiar TTS y reconocimiento
  useEffect(() => {
    if (!open) {
      stopListening();
      stopSpeaking();
      setVoiceMode(false);
    }
  }, [open]);

  if (!open) return null;

  // ── Envío de texto ─────────────────────────────────────────────────────────

  async function handleSend(overrideText?: string) {
    const msg = (overrideText ?? texto).trim();
    if (!msg || streaming) return;
    setTexto('');
    await send(msg);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  // ── Voz: reconocimiento ─────────────────────────────────────────────────────

  function startListening() {
    if (!hasSpeechRecognition || listening) return;
    stopSpeaking();

    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recog = new SR();
    recog.lang = 'es-MX';
    recog.continuous = false;
    recog.interimResults = true;
    finalTextRef.current = '';

    recog.onresult = (evt: any) => {
      let final = '';
      let interim = '';
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        if (evt.results[i].isFinal) final += evt.results[i][0].transcript;
        else interim += evt.results[i][0].transcript;
      }
      if (final) finalTextRef.current = final;
      setTexto(finalTextRef.current || interim);
    };

    recog.onerror = (evt: any) => {
      if (evt.error !== 'no-speech') console.warn('[Ikal voice] error:', evt.error);
      setListening(false);
    };

    recog.onend = () => {
      setListening(false);
      const captured = finalTextRef.current.trim();
      finalTextRef.current = '';
      if (captured) {
        setTexto('');
        send(captured);
      }
    };

    recog.start();
    recognRef.current = recog;
    setListening(true);
  }

  function stopListening() {
    recognRef.current?.stop();
    recognRef.current = null;
    setListening(false);
  }

  // ── Voz: síntesis ──────────────────────────────────────────────────────────

  function speakIkal(text: string) {
    if (!hasSpeechSynthesis) return;
    stopSpeaking();
    const plain = stripMarkdown(text);
    if (!plain) return;
    const utt = new SpeechSynthesisUtterance(plain);
    utt.lang = 'es-MX';
    utt.rate = 0.95;
    utt.onstart = () => setSpeaking(true);
    utt.onend = () => {
      setSpeaking(false);
      // En modo voz, reiniciar escucha automáticamente
      if (voiceModeRef.current) startListening();
    };
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }

  function stopSpeaking() {
    if (hasSpeechSynthesis) window.speechSynthesis.cancel();
    setSpeaking(false);
  }

  function toggleVoiceMode() {
    if (voiceMode) {
      stopListening();
      stopSpeaking();
      setVoiceMode(false);
    } else {
      setVoiceMode(true);
      // Pequeño delay para que el layout se actualice antes de escuchar
      setTimeout(() => startListening(), 200);
    }
  }

  // ── Estados del orbe ────────────────────────────────────────────────────────

  const orbState: OrbState =
    speaking ? 'talking' :
    streaming ? 'thinking' :
    listening ? 'listening' :
    'idle';

  const statusLabel =
    speaking  ? 'Hablando…' :
    streaming ? 'Pensando…' :
    listening ? 'Escuchando…' :
    contexto?.citaId ? 'Consulta activa' :
    'Asistente médico';

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Mobile overlay */}
      <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={closeIkal} />
    <div
      className={cn(
        'fixed z-50 flex flex-col',
        'bg-white border border-gray-100',
        'transition-all duration-200',
        'inset-x-0 bottom-0 rounded-t-3xl',
        'md:inset-x-auto md:bottom-4 md:w-[380px] md:rounded-2xl',
        chatOpen ? 'md:right-[450px]' : 'md:right-[76px]',
        minimizado ? 'h-auto' : voiceMode ? 'h-[82vh] md:h-[440px]' : 'h-[82vh] md:h-[520px]',
      )}
      style={{
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Drag handle (mobile only) */}
      <div className="flex justify-center pt-3 pb-1 md:hidden flex-shrink-0">
        <div className="w-10 h-1 rounded-full bg-gray-200" />
      </div>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 py-3 flex-shrink-0 cursor-pointer select-none rounded-t-2xl"
        style={{ background: 'linear-gradient(135deg, #3d5e5a 0%, #568f88 100%)' }}
        onClick={() => setMinimizado((m) => !m)}
      >
        <IkalOrb state={orbState} size={28} className="flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-white text-sm font-semibold leading-tight">Ikal</p>
            {voiceMode && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: 'rgba(111,187,175,0.35)', color: '#c4f5ef' }}>
                VOZ
              </span>
            )}
          </div>
          <p className="text-[10px] leading-tight" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {statusLabel}
          </p>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
          {/* Toggle voz */}
          {hasSpeechRecognition && (
            <button
              onClick={toggleVoiceMode}
              className="p-1.5 rounded-lg transition-colors"
              style={voiceMode
                ? { backgroundColor: 'rgba(111,187,175,0.4)', color: 'white' }
                : { color: 'rgba(255,255,255,0.6)' }}
              onMouseEnter={e => { if (!voiceMode) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
              onMouseLeave={e => { if (!voiceMode) e.currentTarget.style.backgroundColor = 'transparent'; }}
              title={voiceMode ? 'Salir del modo voz' : 'Modo conversación de voz'}
            >
              {voiceMode ? <Mic size={13} /> : <MicOff size={13} />}
            </button>
          )}
          {/* Toggle TTS */}
          {hasSpeechSynthesis && (
            <button
              onClick={() => { setVoiceOutput(v => !v); if (speaking) stopSpeaking(); }}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: voiceOutput ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.35)' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              title={voiceOutput ? 'Silenciar respuestas' : 'Activar voz de Ikal'}
            >
              {voiceOutput ? <Volume2 size={13} /> : <VolumeX size={13} />}
            </button>
          )}
          <button
            onClick={() => resetConversacion()}
            className="p-1 rounded-lg text-white/70 transition-colors hover:bg-white/20"
            title="Nueva conversación"
          >
            <Trash2 size={12} />
          </button>
          <button
            onClick={() => setMinimizado((m) => !m)}
            className="p-1 rounded-lg text-white/70 transition-colors hover:bg-white/20"
          >
            <Minus size={13} />
          </button>
          <button
            onClick={() => closeIkal()}
            className="p-1 rounded-lg text-white/70 transition-colors hover:bg-white/20"
          >
            <X size={13} />
          </button>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      {!minimizado && (
        <div className="flex-1 overflow-hidden flex flex-col rounded-b-2xl">

          {/* ── MODO VOZ ── */}
          {voiceMode ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 py-4">
              {/* Orbe grande */}
              <IkalOrb state={orbState} size={96} />

              {/* Estado */}
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-800">
                  {speaking ? 'Ikal está hablando…' : listening ? 'Te estoy escuchando' : streaming ? 'Procesando…' : 'Listo para escuchar'}
                </p>
                {texto && !streaming && (
                  <p className="text-xs text-gray-400 mt-1 max-w-[260px] truncate">"{texto}"</p>
                )}
              </div>

              {/* Último mensaje de Ikal */}
              {messages.length > 0 && (() => {
                const last = [...messages].reverse().find(m => m.role === 'assistant');
                if (!last) return null;
                return (
                  <div className="w-full rounded-xl px-4 py-3 text-xs text-gray-600 leading-relaxed max-h-[90px] overflow-y-auto"
                    style={{ backgroundColor: 'rgba(111,187,175,0.08)', border: '1px solid rgba(111,187,175,0.2)' }}>
                    {last.text.slice(0, 300)}{last.text.length > 300 ? '…' : ''}
                  </div>
                );
              })()}

              {/* Controles de voz */}
              <div className="flex gap-3">
                <button
                  onClick={() => listening ? stopListening() : startListening()}
                  disabled={streaming || speaking}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-40"
                  style={{ backgroundColor: listening ? '#C1694F' : '#3d5e5a' }}
                  onMouseEnter={e => { if (!streaming && !speaking) e.currentTarget.style.backgroundColor = listening ? '#a85640' : '#2e4a47'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = listening ? '#C1694F' : '#3d5e5a'; }}
                >
                  {listening ? <><MicOff size={15} /> Detener</> : <><Mic size={15} /> Hablar</>}
                </button>
                {speaking && (
                  <button
                    onClick={stopSpeaking}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                  >
                    <VolumeX size={15} /> Silenciar
                  </button>
                )}
              </div>
            </div>
          ) : (
            /* ── MODO CHAT ── */
            <>
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                {messages.length === 0 && !streaming && (
                  <div className="flex flex-col items-center justify-center h-full text-center py-10 px-6">
                    <IkalOrb size={56} className="mb-4 opacity-80" />
                    <p className="text-sm font-semibold text-gray-700">Hola, soy Ikal</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {contexto?.citaId
                        ? 'Tengo el contexto de la consulta activa. Pregúntame sobre el paciente, diagnósticos, recetas y más.'
                        : 'Tu asistente médico inteligente. Puedo consultar expedientes, agenda, emitir recetas y más.'}
                    </p>
                    {hasSpeechRecognition && (
                      <button
                        onClick={toggleVoiceMode}
                        className="mt-4 flex items-center gap-2 text-xs px-4 py-2 rounded-xl border transition-colors"
                        style={{ borderColor: 'rgba(111,187,175,0.4)', color: '#3d5e5a' }}
                      >
                        <Mic size={12} /> Modo conversación de voz
                      </button>
                    )}
                  </div>
                )}

                {messages.map((msg) => (
                  <div key={msg.id} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                    {msg.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-full flex-shrink-0 mr-2 mt-0.5">
                        <IkalOrb size={24} />
                      </div>
                    )}
                    <div
                      className={cn('max-w-[85%] px-3 py-2 rounded-2xl', msg.role === 'user' ? 'text-white rounded-br-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm')}
                      style={msg.role === 'user' ? { backgroundColor: '#3d5e5a' } : undefined}
                    >
                      {msg.role === 'assistant' ? <Markdown text={msg.text} /> : <p className="text-sm leading-relaxed">{msg.text}</p>}
                    </div>
                  </div>
                ))}

                {streaming && (
                  <div className="flex justify-start">
                    <div className="w-6 h-6 rounded-full flex-shrink-0 mr-2 mt-0.5">
                      <IkalOrb size={24} state="thinking" />
                    </div>
                    <div className="max-w-[85%] px-3 py-2 rounded-2xl bg-gray-100 text-gray-900 rounded-bl-sm">
                      {streamBuffer
                        ? <Markdown text={streamBuffer} />
                        : <span className="flex items-center gap-1.5 text-sm text-gray-400"><Loader2 size={12} className="animate-spin" /> Pensando…</span>}
                    </div>
                  </div>
                )}

                <IkalConfirmCard />
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-gray-100 flex gap-2 items-end flex-shrink-0">
                {/* Mic button (modo texto) */}
                {hasSpeechRecognition && (
                  <button
                    onMouseDown={startListening}
                    onMouseUp={() => { if (listening) stopListening(); }}
                    onTouchStart={startListening}
                    onTouchEnd={() => { if (listening) stopListening(); }}
                    disabled={streaming}
                    className="w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors disabled:opacity-40"
                    style={listening
                      ? { backgroundColor: '#C1694F', color: 'white' }
                      : { backgroundColor: '#f3f4f6', color: '#6b7280' }}
                    title="Mantén presionado para hablar"
                  >
                    <Mic size={15} />
                  </button>
                )}

                <textarea
                  ref={inputRef}
                  value={texto}
                  onChange={(e) => {
                    setTexto(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={listening ? 'Escuchando…' : 'Escribe o habla…'}
                  rows={1}
                  className="flex-1 resize-none bg-gray-50 rounded-xl px-3 py-2 text-sm text-gray-900 placeholder-gray-400 outline-none border border-gray-200 transition-colors"
                  style={{ maxHeight: 100, minHeight: 36 }}
                  onFocus={e => { e.target.style.borderColor = '#6FBBAF'; }}
                  onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                  disabled={streaming || listening}
                />

                <button
                  onClick={() => handleSend()}
                  disabled={!texto.trim() || streaming}
                  className="w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors disabled:cursor-not-allowed"
                  style={texto.trim() && !streaming
                    ? { backgroundColor: '#3d5e5a', color: 'white' }
                    : { backgroundColor: '#f3f4f6', color: '#d1d5db' }}
                  onMouseEnter={e => { if (texto.trim() && !streaming) e.currentTarget.style.backgroundColor = '#2e4a47'; }}
                  onMouseLeave={e => { if (texto.trim() && !streaming) e.currentTarget.style.backgroundColor = '#3d5e5a'; }}
                >
                  {streaming ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
    </>
  );
}
